import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import { getKitsConProductos } from '../../lib/portalClienteApi'
import { consolidarProductosKit, presentacionTotal, presentacionALitros } from '../../lib/kitDisplay'
import { supabase } from '../../lib/supabase'

const TIER_LABEL = { essential: 'Essential', premium: 'Premium', comercial: 'Comercial' }
const TIER_COLOR = {
  essential: { bg: '#e6fafc', color: '#0f7480' },
  premium:   { bg: '#fef3c7', color: '#92400e' },
  comercial: { bg: '#ede9fe', color: '#5b21b6' },
}

// Pantallas donde se muestra cada kit a clientes/visitantes
const PANTALLAS = [
  { titulo: 'Tienda pública · grid Kits', url: 'https://tienda.limpack.uy/kits', ok: true, nota: 'Live desde DB (cliente_get_kits_con_productos) vía kitViewModel. Desde 2026-07-24.' },
  { titulo: 'Tienda pública · detalle de kit', url: 'https://tienda.limpack.uy/kit/familiar', ok: true, nota: 'Productos + precios live desde DB. Copy marketing (descLarga/FAQ) estático. Desde 2026-07-24.' },
  { titulo: 'Tienda (modal kit)',         url: '/cliente/tienda',           ok: true },
  { titulo: 'Nueva suscripción (paso 2)', url: '/cliente/nueva-suscripcion', ok: true },
  { titulo: 'Probar kit',                 url: '/cliente/probar-kit',        ok: true },
  { titulo: 'Mi Plan (cliente actual)',   url: '/cliente/mi-plan',           ok: true },
  // Desde 2026-06-04: la landing es un Worker SSR que lee Supabase via RPC
  // `landing_get_kits()` y cachea 5min. Cualquier cambio en /admin/kits o
  // en productos.nombre_landing se ve en limpack.uy en ≤5 minutos.
  { titulo: 'Landing pública',            url: 'https://limpack.uy/',        ok: true, nota: 'Worker SSR auto-sync con DB (cache 5min). Cambios aparecen en ≤5min.' },
]

function fmtMonto(n) { return '$' + Number(n || 0).toLocaleString('es-UY', { maximumFractionDigits: 0 }) }
function fmtFechaCorta(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleDateString('es-UY', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
}

export default function AuditoriaKits() {
  const [kits, setKits] = useState([])
  const [cargando, setCargando] = useState(true)
  const [ultimoCambio, setUltimoCambio] = useState(null)
  const [tickRealtime, setTickRealtime] = useState(false)
  const debounceRef = useRef(null)

  useEffect(() => { cargar() }, [])

  async function cargar() {
    setCargando(true)
    const k = await getKitsConProductos()
    setKits(k || [])
    setCargando(false)
  }

  // Realtime: detectar cambios en kits/kit_items/productos y refrescar.
  useEffect(() => {
    const refrescar = (table, payload) => {
      clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => {
        cargar()
        setUltimoCambio({ table, ts: new Date() })
        setTickRealtime(true)
        setTimeout(() => setTickRealtime(false), 1800)
      }, 400)
    }
    const ch = supabase
      .channel('audit:kits')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'kits' },      p => refrescar('kits', p))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'kit_items' }, p => refrescar('kit_items', p))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'productos' }, p => refrescar('productos', p))
      .subscribe()
    return () => { supabase.removeChannel(ch); clearTimeout(debounceRef.current) }
  }, [])

  return (
    <AdminLayout title="Auditoría de kits">
      <div className="ak-wrap">
        <div className="ak-header">
          <div>
            <h1>Auditoría de kits</h1>
            <p>Fuente única (DB) y dónde aparece cada kit. Cambios en admin se reflejan acá en vivo.</p>
          </div>
          <div className={`ak-status ${tickRealtime ? 'pulse' : ''}`}>
            <span className="ak-status-dot"></span>
            Live realtime
            {ultimoCambio && (
              <div className="ak-status-sub">
                Último cambio: <strong>{ultimoCambio.table}</strong> · {fmtFechaCorta(ultimoCambio.ts)}
              </div>
            )}
          </div>
        </div>

        {/* Tabla de fuentes — los lugares donde aparece un kit */}
        <div className="ak-fuentes">
          <h2>Pantallas donde aparece la composición</h2>
          <table>
            <thead><tr><th>Pantalla</th><th>Sincronía</th><th>Link</th></tr></thead>
            <tbody>
              {PANTALLAS.map((p, i) => (
                <tr key={i} className={p.ok ? 'ok' : 'warn'}>
                  <td>{p.titulo}</td>
                  <td>
                    {p.ok
                      ? <span className="ak-pill ok">✓ Live desde DB</span>
                      : <span className="ak-pill warn">⚠ Hardcoded</span>}
                    {p.nota && <div className="ak-nota">{p.nota}</div>}
                  </td>
                  <td><a href={p.url} target="_blank" rel="noopener">Abrir →</a></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Cards de cada kit con composición consolidada y cruda */}
        {cargando ? (
          <div className="ak-loading">Cargando kits…</div>
        ) : (
          <div className="ak-kits">
            {kits.map(k => {
              const productosReales = (k.productos || []).filter(p => !p.es_regalo)
              const consolidado = consolidarProductosKit(productosReales)
              const tieneRegalo = (k.productos || []).some(p => p.es_regalo)
              const totalLitros = productosReales.reduce(
                (s, p) => s + presentacionALitros(p.presentacion) * Number(p.cantidad || 1), 0
              )
              const totalItems = productosReales.reduce((s, p) => s + Number(p.cantidad || 1), 0)
              // Suma de productos sueltos (precio_publico × cantidad) — ignora regalos
              const sumaSueltos = productosReales.reduce(
                (s, p) => s + (Number(p.precio_publico) || 0) * Number(p.cantidad || 1), 0
              )
              // Validación: el kit (compra única) debe estar por DEBAJO de la suma de sueltos
              const precioUnica = Number(k.precio_unica) || 0
              const precioSusc = Number(k.precio_susc) || 0
              const ahorroUnica = sumaSueltos > 0 && precioUnica > 0 ? sumaSueltos - precioUnica : 0
              const ahorroUnicaPct = sumaSueltos > 0 && precioUnica > 0
                ? Math.round((ahorroUnica / sumaSueltos) * 100) : 0
              const ahorroSusc = sumaSueltos > 0 && precioSusc > 0 ? sumaSueltos - precioSusc : 0
              const ahorroSuscPct = sumaSueltos > 0 && precioSusc > 0
                ? Math.round((ahorroSusc / sumaSueltos) * 100) : 0
              const alertaUnica = precioUnica > 0 && sumaSueltos > 0 && precioUnica >= sumaSueltos
              const alertaSusc = precioSusc > 0 && sumaSueltos > 0 && precioSusc >= sumaSueltos
              return (
                <div key={k.codigo} className="ak-kit">
                  <div className="ak-kit-head">
                    <div>
                      <div className="ak-kit-nombre">
                        {k.nombre}
                        <span className="ak-tier" style={{ background: TIER_COLOR[k.tier]?.bg, color: TIER_COLOR[k.tier]?.color }}>
                          {TIER_LABEL[k.tier] || k.tier}
                        </span>
                      </div>
                      <div className="ak-kit-codigo">{k.codigo} · {k.personas || '—'} personas</div>
                    </div>
                    <Link to={`/admin/kits/${k.codigo}`} className="ak-edit-btn">Editar →</Link>
                  </div>

                  <div className="ak-kit-stats">
                    <div className={alertaSusc ? 'ak-stat-alert' : ''}>
                      <span>Precio susc</span>
                      <strong>{fmtMonto(k.precio_susc)}</strong>
                      {ahorroSuscPct > 0 && <em className="ak-ahorro">−{ahorroSuscPct}%</em>}
                      {alertaSusc && <em className="ak-warn">🚨 ≥ sueltos</em>}
                    </div>
                    <div className={alertaUnica ? 'ak-stat-alert' : ''}>
                      <span>Precio única</span>
                      <strong>{fmtMonto(k.precio_unica)}</strong>
                      {ahorroUnicaPct > 0 && <em className="ak-ahorro">−{ahorroUnicaPct}%</em>}
                      {alertaUnica && <em className="ak-warn">🚨 ≥ sueltos</em>}
                    </div>
                    <div>
                      <span>Suma sueltos</span>
                      <strong>{sumaSueltos > 0 ? fmtMonto(sumaSueltos) : '—'}</strong>
                      <em className="ak-hint">si compraran 1×1</em>
                    </div>
                    <div><span>Productos</span><strong>{totalItems}</strong></div>
                    <div><span>Litros</span><strong>{totalLitros > 0 ? totalLitros.toFixed(1).replace('.', ',') + ' L' : '—'}</strong></div>
                  </div>

                  <div className="ak-comp">
                    <div className="ak-comp-col">
                      <h3>👀 Cómo lo ve el cliente (consolidado)</h3>
                      <ul>
                        {consolidado.map((p, i) => (
                          <li key={i} className={p.es_consolidado ? 'cons' : ''}>
                            {!p.es_consolidado && <span className="ak-cant">{p.cantidad}×</span>}
                            <span>{p.nombre}</span>
                            {(() => {
                              const pres = p.es_consolidado ? p.presentacion : presentacionTotal(p.cantidad, p.presentacion)
                              return pres ? <span className="ak-pres"> · {pres}</span> : null
                            })()}
                            {!p.es_consolidado && p.marca && <span className="ak-marca"> · {p.marca}</span>}
                          </li>
                        ))}
                        {tieneRegalo && (
                          <li className="ak-regalo">🎁 Regalo sorpresa del mes (no visible al cliente)</li>
                        )}
                      </ul>
                    </div>
                    <div className="ak-comp-col">
                      <h3>🔧 Cómo está en DB (sin consolidar)</h3>
                      <ul>
                        {productosReales.map((p, i) => (
                          <li key={i}>
                            <span className="ak-cant">{p.cantidad}×</span>
                            <span>{p.nombre}</span>
                            {p.presentacion && <span className="ak-pres"> · {p.presentacion}</span>}
                            {p.marca && <span className="ak-marca"> · {p.marca}</span>}
                            <span className="ak-sku"> [{p.sku}]</span>
                          </li>
                        ))}
                        {tieneRegalo && (k.productos || []).filter(p => p.es_regalo).map((p, i) => (
                          <li key={`r-${i}`} className="ak-regalo">
                            🎁 {p.nombre} · {p.presentacion} <span className="ak-sku"> [{p.sku}]</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      <style>{css}</style>
    </AdminLayout>
  )
}

const css = `
.ak-wrap { padding: 24px; max-width: 1300px; margin: 0 auto }
.ak-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 20px; margin-bottom: 22px; flex-wrap: wrap }
.ak-header h1 { font-size: 26px; font-weight: 900; color: #1a3461; margin: 0 0 6px; letter-spacing: -.02em }
.ak-header p { font-size: 13px; color: #64748b; margin: 0; line-height: 1.5 }

.ak-status {
  background: #f0fdf4; border: 1.5px solid #86efac; border-radius: 12px;
  padding: 10px 16px; font-size: 12.5px; color: #065f46; font-weight: 700;
  display: flex; align-items: center; gap: 10px; transition: background .3s, border-color .3s;
}
.ak-status.pulse { background: #bbf7d0; border-color: #16a34a }
.ak-status-dot {
  width: 10px; height: 10px; border-radius: 50%; background: #16a34a;
  box-shadow: 0 0 0 0 rgba(22,163,74,.5); animation: pulse 2s infinite;
}
@keyframes pulse {
  0% { box-shadow: 0 0 0 0 rgba(22,163,74,.5) }
  70% { box-shadow: 0 0 0 8px rgba(22,163,74,0) }
  100% { box-shadow: 0 0 0 0 rgba(22,163,74,0) }
}
.ak-status-sub { font-size: 11px; color: #047857; font-weight: 600; margin-top: 2px }

.ak-fuentes { background: #fff; border: 1px solid #e2e8f0; border-radius: 14px; padding: 16px 20px; margin-bottom: 24px }
.ak-fuentes h2 { font-size: 14px; font-weight: 900; color: #1a3461; margin: 0 0 12px; text-transform: uppercase; letter-spacing: .04em }
.ak-fuentes table { width: 100%; border-collapse: collapse; font-size: 13px }
.ak-fuentes th { text-align: left; padding: 8px 10px; background: #f8fafc; color: #64748b; font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: .06em }
.ak-fuentes td { padding: 10px; border-bottom: 1px solid #f1f5f9; color: #1a3461 }
.ak-fuentes tr:last-child td { border-bottom: 0 }
.ak-fuentes tr.warn td { background: #fffbeb }
.ak-fuentes a { color: #00a8b5; font-weight: 700; text-decoration: none }
.ak-fuentes a:hover { text-decoration: underline }

.ak-pill { padding: 3px 9px; border-radius: 999px; font-size: 11px; font-weight: 800 }
.ak-pill.ok { background: #dcfce7; color: #15803d }
.ak-pill.warn { background: #fef3c7; color: #92400e }
.ak-nota { font-size: 11.5px; color: #92400e; margin-top: 4px; line-height: 1.4 }

.ak-loading { text-align: center; padding: 40px; color: #64748b }

.ak-kits { display: flex; flex-direction: column; gap: 14px }
.ak-kit { background: #fff; border: 1px solid #e2e8f0; border-radius: 14px; padding: 18px 20px }
.ak-kit-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; margin-bottom: 12px; flex-wrap: wrap }
.ak-kit-nombre { font-size: 17px; font-weight: 900; color: #1a3461; display: flex; align-items: center; gap: 10px; flex-wrap: wrap }
.ak-tier { padding: 3px 10px; border-radius: 999px; font-size: 10.5px; font-weight: 800; text-transform: uppercase; letter-spacing: .06em }
.ak-kit-codigo { font-size: 12px; color: #64748b; margin-top: 2px; font-family: ui-monospace, monospace }
.ak-edit-btn {
  padding: 7px 14px; background: #1a3461; color: #fff; border-radius: 8px;
  font-size: 12px; font-weight: 700; text-decoration: none;
}

.ak-kit-stats {
  display: grid; grid-template-columns: repeat(5, 1fr); gap: 8px;
  padding: 10px 14px; background: #f8fafc; border-radius: 10px; margin-bottom: 14px;
}
.ak-kit-stats > div { display: flex; flex-direction: column; padding: 6px 8px; border-radius: 8px }
.ak-kit-stats span { font-size: 10.5px; color: #64748b; text-transform: uppercase; letter-spacing: .06em; font-weight: 700 }
.ak-kit-stats strong { font-size: 14px; color: #1a3461; font-weight: 800; margin-top: 2px }
.ak-kit-stats em { font-style: normal; font-size: 10.5px; margin-top: 2px }
.ak-ahorro { color: #16a34a; font-weight: 800 }
.ak-warn { color: #dc2626; font-weight: 800 }
.ak-hint { color: #94a3b8; font-style: italic !important }
.ak-stat-alert { background: #fef2f2; border: 1px solid #fecaca }
@media (max-width: 900px) { .ak-kit-stats { grid-template-columns: repeat(3, 1fr) } }
@media (max-width: 600px) { .ak-kit-stats { grid-template-columns: 1fr 1fr } }

.ak-comp { display: grid; grid-template-columns: 1fr 1fr; gap: 14px }
@media (max-width: 800px) { .ak-comp { grid-template-columns: 1fr } }
.ak-comp-col h3 { font-size: 11.5px; font-weight: 900; color: #64748b; margin: 0 0 8px; text-transform: uppercase; letter-spacing: .06em }
.ak-comp-col ul { list-style: none; padding: 0; margin: 0; font-size: 13px; line-height: 1.55; color: #1a3461 }
.ak-comp-col li { padding: 4px 0; border-bottom: 1px dashed #f1f5f9 }
.ak-comp-col li:last-child { border-bottom: 0 }
.ak-comp-col li.cons { background: #f0fdf4; padding: 6px 8px; border-radius: 6px; margin: 2px 0; border-bottom: 0; font-weight: 700 }
.ak-comp-col li.ak-regalo { background: #fffbeb; padding: 6px 8px; border-radius: 6px; margin: 2px 0; border-bottom: 0; color: #78350f; font-weight: 700 }
.ak-cant { font-weight: 800; color: #00a8b5; margin-right: 4px }
.ak-pres { color: #475569; font-weight: 600 }
.ak-marca { color: #64748b; font-style: italic }
.ak-sku { color: #cbd5e1; font-family: ui-monospace, monospace; font-size: 11px }
`
